import { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { log } from "../../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RatingWidget({ compact = false }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ratings/me`, { withCredentials: true })
      .then(({ data }) => {
        if (data.rating) {
          setSaved(data.rating);
          setStars(data.rating.stars);
          setComment(data.rating.comment || "");
        }
      })
      .catch((err) => log.warn("rating load failed", err));
  }, []);

  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/ratings`, { stars, comment: comment.trim() || null }, { withCredentials: true });
      setSaved({ stars, comment });
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save rating");
    }
    setBusy(false);
  };

  return (
    <div data-testid="rating-widget" className={`rounded-xl border-2 border-[var(--c-border)] bg-[var(--c-bg)] text-left ${compact ? "mt-1.5 p-3" : "mt-6 p-4"}`}>
      <p className={`font-heading font-black text-[var(--c-text)] ${compact ? "text-xs" : "text-sm"}`}>{saved ? "Thanks for rating the guide!" : "How was the guide?"}</p>
      <div className="mt-2 flex items-center gap-1" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            data-testid={`rating-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            aria-pressed={stars === n}
            onMouseEnter={() => setHover(n)}
            onClick={() => setStars(n)}
            className="rounded p-0.5 transition-transform hover:scale-110"
          >
            <Star className={`${compact ? "h-5 w-5" : "h-7 w-7"} ${(hover || stars) >= n ? "fill-[#F5B301] text-[#F5B301]" : "text-[var(--c-muted)]"}`} />
          </button>
        ))}
        {stars > 0 && <span className="ml-2 text-xs font-semibold text-[var(--c-muted)]" data-testid="rating-value">{stars}/5</span>}
      </div>
      {(!compact || stars > 0) && (
        <>
          <textarea
        data-testid="rating-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder="One line on what helped most (optional, may be quoted on the site)"
        className="mt-3 w-full resize-none rounded-lg border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2 text-xs text-[var(--c-text)] outline-none placeholder:text-[var(--c-muted)] focus:border-[#1E3A8A]"
      />
      <button
        data-testid="rating-submit"
        onClick={submit}
        disabled={!stars || busy}
        className="mt-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-4 py-2 text-xs font-bold text-white shadow-[2px_2px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
      >
        {saved ? "Update rating" : "Submit rating"}
      </button>
        </>
      )}
    </div>
  );
}
